import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import "@/styles/pages.css";

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  trial: "Trial",
  pro: "Starter",
  agent: "Agency",
};

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [countdown, setCountdown] = useState(5);
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const purchaseTracked = useRef(false);

  useEffect(() => {
    if (!sessionId) return;

    setVerifying(true);
    fetch(`/api/stripe/checkout/verify?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.verified) {
          setVerified(true);
          refresh();

          if (data.payment_status === "paid") {
            const storageKey = `ga4_purchase_${sessionId}`;
            if (window.gtag && !purchaseTracked.current && !sessionStorage.getItem(storageKey)) {
              purchaseTracked.current = true;
              sessionStorage.setItem(storageKey, "1");
              const plan = data.plan || "unknown";
              const billingInterval = data.interval || "monthly";
              const displayName = PLAN_DISPLAY_NAMES[plan] ?? plan;
              const value = typeof data.amount_total === "number" ? data.amount_total / 100 : 0;
              const currency = data.currency || "USD";
              window.gtag("event", "purchase", {
                transaction_id: sessionId,
                currency: currency,
                value: value,
                items: [{
                  item_id: plan,
                  item_name: displayName,
                  price: value,
                  quantity: 1,
                  item_category: billingInterval,
                }],
              });
            }
          }
        } else {
          setError(data.error || "Could not verify payment");
        }
      })
      .catch(() => setError("Failed to verify payment"))
      .finally(() => setVerifying(false));
  }, [sessionId]);

  useEffect(() => {
    if (!verified) return;
    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/account/billing");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [verified]);

  if (sessionId && verifying) {
    return (
      <div className="centered-page">
        <div className="centered-card" style={{ maxWidth: 480 }}>
          <h1>Verifying payment...</h1>
          <p className="card-subtitle">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (sessionId && error) {
    return (
      <div className="centered-page">
        <div className="centered-card" style={{ maxWidth: 480 }}>
          <h1>Payment Issue</h1>
          <p className="card-subtitle">{error}</p>
          <Link to="/pricing" className="btn btn-primary" style={{ width: "100%", marginTop: 16 }}>
            Back to Pricing
          </Link>
        </div>
      </div>
    );
  }

  if (sessionId && verified) {
    return (
      <div className="centered-page">
        <div className="centered-card" style={{ maxWidth: 480 }}>
          <div className="card-icon" style={{ color: "#22c55e" }}>
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1>Payment Successful!</h1>
          <p className="card-subtitle">
            Your subscription is now active. Thank you for upgrading!
          </p>
          <p className="card-subtitle">
            Redirecting to billing in {countdown} seconds...
          </p>
          <Link to="/account/billing" className="btn btn-primary" style={{ width: "100%", marginTop: 16 }}>
            Go to Billing
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="centered-page">
      <div className="centered-card" style={{ maxWidth: 480 }}>
        <div className="card-icon">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        </div>
        <h1>Checkout</h1>
        <p className="card-subtitle">
          Choose a plan from our pricing page to get started.
        </p>
        <Link to="/pricing" className="btn btn-primary" style={{ width: "100%", marginTop: 16 }}>
          View Plans
        </Link>
        <p className="card-footer">
          <Link to="/">Back to Home</Link>
        </p>
      </div>
    </div>
  );
}
